// @ts-nocheck
/*
 * Port of views/dashboard/voice-plan/edit.js (origin/main-dev, 491 lines).
 * BYTE-IDENTICAL — GET pre-populates flattenPlansToArray(resp.plans),
 * initialSnapshot for Reset button, autoExpandExistingRows locks all
 * loaded rows, patchApiCall (not post) on update, canUseVoicePlanType
 * ACL check per plan. Chrome swap: v1 <Card> → shadcn.
 */
import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { getApiCall } from '@/service/httpCall';
import { patchApiCall } from '@/service/patchApiCall';
import { API_VOICE_PLAN_EDIT, API_VOICE_PLAN_GET_BY_ID } from '@/service/url';
import { useNavigate, useParams } from 'react-router-dom';
import { ROUTES, USER_ROLES } from '@/utilities/constant';
import {
  getAllowedVoicePlanDirections,
  canUseVoicePlanType,
  parseModuleOperations,
} from '@/utilities/voicePlanAcl';
import RenderVoicePlanForm, { MAX_PULSE_PRICE_DECIMALS } from '@/features/voice-plan/VoicePlanForm';
import {
  getVoicePlanKey,
  groupPlansForStorage,
  flattenPlansToArray,
  areAllPlansComplete,
} from '@/utilities/voicePlanUtils';
import { useAuth, errorCheck } from '@/views/dashboard/auth/auth-utils';
import { getUser } from '@/utilities/setting';
import '@/features/voice-plan/voice-plan-form.scss';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { Button as PageButton } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function VoicePlanEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { checkAuth } = useAuth();

  const [planName, setPlanName] = useState('');
  const [isDemo, setIsDemo] = useState(false);
  const [planType, setPlanType] = useState('');
  const [legType, setLegType] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastProps, setToastProps] = useState(null);
  const [buttonDisable, setButtonDisable] = useState(false);
  const [userPulseDuration, setUserPulseDuration] = useState(null);
  const [userPulsePrice, setUserPulsePrice] = useState(Infinity);
  const [plansData, setPlansData] = useState([]);
  const [currentPlan, setCurrentPlan] = useState([]);
  const [planNameError, setPlanNameError] = useState('');
  const [loading, setLoading] = useState(true);
  const [allowedPlanTypes, setAllowedPlanTypes] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [initialSnapshot, setInitialSnapshot] = useState(null);
  const [lockedRowKeys, setLockedRowKeys] = useState([]);
  const [frozenLegKeys, setFrozenLegKeys] = useState([]);

  useEffect(() => {
    checkAuth(setToastProps, setShowToast);
  }, []);

  useEffect(() => {
    const user = getUser();
    if (user?.voice_plan_id) {
      setUserPulseDuration(user.voice_plan_id.pulse_duration);
      setUserPulsePrice(user.voice_plan_id.price);
    }
    const superAdmin = user?.role === USER_ROLES.SUPERADMIN;
    setIsSuperAdmin(superAdmin);
    setAllowedPlanTypes(
      getAllowedVoicePlanDirections(user?.acl_settings?.module_operations, user?.role, superAdmin),
    );

    getApiCall(`${API_VOICE_PLAN_GET_BY_ID}/${id}`)
      .then((resp) => {
        const flatPlans = flattenPlansToArray(resp.plans);
        setPlanName(resp.plan_name);
        setIsDemo(!!resp.is_demo);
        setPlansData(flatPlans);
        setCurrentPlan(flatPlans.map((p) => getVoicePlanKey(p)));
        setLockedRowKeys(flatPlans.map((p) => getVoicePlanKey(p)));
        if (resp.is_allocated == 1 && !resp.is_demo) {
          setFrozenLegKeys(flatPlans.map((p) => getVoicePlanKey(p)));
        }
        setInitialSnapshot({
          planName: resp.plan_name,
          plans: flatPlans,
          currentPlan: flatPlans.map((p) => getVoicePlanKey(p)),
        });
      })
      .catch((err) => {
        toast.error('Failed to load plan data');
        console.error(err);
      })
      .finally(() => setLoading(false));
  }, [id]);

  function validateForm() {
    if (!planName) {
      toast.warning('Plan Name required');
      return false;
    }

    if (!areAllPlansComplete(plansData)) {
      toast.warning('Fill pulse duration and price for all legs to continue');
      return false;
    }

    if (
      plansData.some((plan) => {
        const key = getVoicePlanKey(plan);
        return !lockedRowKeys.includes(key) && !frozenLegKeys.includes(key);
      })
    ) {
      toast.warning('Click + on all legs before updating the voice plan');
      return false;
    }

    const submittablePlans = plansData;

    if (
      submittablePlans.some(
        (plan) =>
          !canUseVoicePlanType(
            parseModuleOperations(getUser()?.acl_settings?.module_operations),
            plan.plan_type,
            { isSuperAdmin },
          ),
      )
    ) {
      toast.warning('One or more plan types are not allowed for your role.');
      return false;
    }

    for (let i = 0; i < submittablePlans.length; i++) {
      const plan = submittablePlans[i];
      const row = i + 1;

      if (plan.pulse_price === '' || plan.pulse_price === null || plan.pulse_price === undefined) {
        toast.warning(`Enter pulse price for row ${row}`);
        return false;
      }

      const priceStr = String(plan.pulse_price);
      const decimalParts = priceStr.split('.');
      if (decimalParts.length > 1 && decimalParts[1].length > MAX_PULSE_PRICE_DECIMALS) {
        toast.warning(
          `Price at row ${row} must have maximum ${MAX_PULSE_PRICE_DECIMALS} decimal places`,
        );
        return false;
      }

      if (plan.plan_type !== 'MISSED' && !plan.pulse_duration) {
        toast.warning(`Select pulse duration for row ${row}`);
        return false;
      }
    }

    return true;
  }

  function submitForm(e) {
    e.preventDefault();
    setButtonDisable(true);

    if (!validateForm()) {
      setButtonDisable(false);
      return;
    }

    const normalizedPlans = plansData.map((plan) => ({
      ...plan,
      pulse_price:
        typeof plan.pulse_price === 'string' ? parseFloat(plan.pulse_price) : Number(plan.pulse_price),
      pulse_duration:
        typeof plan.pulse_duration === 'string'
          ? parseInt(plan.pulse_duration, 10)
          : Number(plan.pulse_duration),
    }));

    const params = {
      plan_name: planName,
      plans: groupPlansForStorage(normalizedPlans),
      is_demo: isDemo,
    };

    patchApiCall(`${API_VOICE_PLAN_EDIT}/${id}`, params)
      .then(() => {
        toast.success('Voice plan updated successfully');
        setTimeout(() => {
          navigate(`/${ROUTES.VOICE_PLAN.LIST}`);
        }, 1500);
      })
      .catch((err) => {
        if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
          errorCheck(err, navigate, setToastProps, setShowToast);
        }
        setButtonDisable(false);
        toast.error(friendlyApiError(err) || 'Failed to update voice plan');
      });
  }

  function handleReset() {
    if (initialSnapshot) {
      setPlanName(initialSnapshot.planName);
      setPlansData(initialSnapshot.plans);
      setCurrentPlan(initialSnapshot.currentPlan);
      setLockedRowKeys(initialSnapshot.currentPlan);
    }
    setPlanType('');
    setLegType('');
    setPlanNameError('');
  }

  if (loading) return <div>Loading...</div>;

  return (
    <ScrollPage>
      <PageHeader
        title="Edit Voice Plan"
        description="Update voice plan details including call types, legs, durations and pricing."
        actions={
          <PageButton asChild variant="outline" size="sm" className="gap-1">
            <Link to={`/${ROUTES.VOICE_PLAN.LIST}`}>
              <ArrowLeft className="h-4 w-4" />
              Back
            </Link>
          </PageButton>
        }
      />
      <Card className="p-4 voice-plan-page">
        <form onSubmit={submitForm}>
                <RenderVoicePlanForm
                  pageTitle="Edit Voice Plan"
                  pageSubtitle="Update voice plan details including call types, legs, durations and pricing."
                  planName={planName}
                  setPlanName={setPlanName}
                  isDemo={isDemo}
                  setIsDemo={setIsDemo}
                  isEdit={true}
                  setUserPulsePrice={setUserPulsePrice}
                  userPulsePrice={userPulsePrice}
                  userPulseDuration={userPulseDuration}
                  setUserPulseDuration={setUserPulseDuration}
                  setPlanType={setPlanType}
                  planType={planType}
                  legType={legType}
                  setLegType={setLegType}
                  setPlansData={setPlansData}
                  plansData={plansData}
                  currentPlan={currentPlan}
                  setCurrentPlan={setCurrentPlan}
                  lockedRowKeys={lockedRowKeys}
                  setLockedRowKeys={setLockedRowKeys}
                  frozenLegKeys={frozenLegKeys}
                  setPlanNameError={setPlanNameError}
                  allowedPlanTypes={allowedPlanTypes}
                  onReset={handleReset}
                  autoExpandExistingRows
                  submitDisabled={buttonDisable}
                  submitLoading={buttonDisable}
          submitLabel="Update Voice Plan"
        />
        </form>
      </Card>
    </ScrollPage>
  );
}
